import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Plus, RefreshCw, Trash2, Edit2, X, Save, Calendar, Pin } from "lucide-react";
import { api } from "../lib/api";
import { HardWipeCard } from "./site-admin/HardWipeCard";
import { RebuildFromCacheCard } from "./site-admin/RebuildFromCacheCard";
import { UpstreamSnapshotsCard } from "./site-admin/UpstreamSnapshotsCard";

/**
 * Site-admin Schedule Console — per-tournament CRUD.
 *
 * Opened as a modal/drawer from the SiteAdmin Leagues table. Allows the site
 * admin to add manual games, edit teams / scores / kickoff time, delete bogus
 * entries, and force-pull from ESPN.
 *
 * All edits flip `is_manual_override=True` server-side so future ESPN syncs
 * won't overwrite them.
 *
 * iter84.58 — Per-match Round Override dropdown. Operator picks a round
 * (R32 / R16 / QF / SF / FINAL / 3RD / Group / Friendly) to pin the
 * `round_label` on a single row; the server writes through to
 * `matches.stage` and reruns `recompute_team_stats`. Pick "(clear)" to
 * let ESPN sync take the row back over.
 */
const STATUSES = [
    { value: "scheduled", label: "Scheduled" },
    { value: "live", label: "Live" },
    { value: "post", label: "Final" },
    { value: "cancelled", label: "Cancelled" },
];

const ROUNDS = ["Group", "R64", "R32", "R16", "QF", "SF", "Final"];

// iter84.58 — Backend `_ROUND_OVERRIDE_CHOICES` mirror. Keep in sync with
// /app/backend/schedule.py if you add/remove a choice.
const OVERRIDE_ROUND_CHOICES = ["R32", "R16", "QF", "SF", "FINAL", "3RD", "Group", "Friendly"];

const _dt = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
};

// HTML datetime-local input wants "YYYY-MM-DDTHH:MM" (no Z, no seconds).
const _toLocalInput = (iso) => {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        const pad = (n) => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch { return ""; }
};

const _fromLocalInput = (local) => local ? new Date(local).toISOString() : null;

export const SiteAdminSchedule = ({ tournamentId, tournament, onClose }) => {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [editingEntry, setEditingEntry] = useState(null);   // null | entry | "new"
    const [espnConfigured, setEspnConfigured] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.getSchedule(tournamentId);
            setEntries(res.entries || []);
            setEspnConfigured(res.espn_configured);
        } catch (e) {
            toast.error("Failed to load schedule");
        }
        setLoading(false);
    }, [tournamentId]);

    useEffect(() => { load(); }, [load]);

    const handleDelete = async (entry) => {
        if (!window.confirm(`Delete this schedule entry?\n\n${entry.home_label} vs ${entry.away_label} — ${_dt(entry.scheduled_at)}`)) return;
        try {
            // Manual override rows live in scheduled_matches; non-manual played rows (source=match)
            // need the existing match-delete path. For v1 we only support deleting via the schedule
            // id, which only works for entries that have a scheduled_matches row.
            // iter84.59.13 — Pre-fix this was `source !== "schedule"`. The backend
            // now returns the doc's real provenance (espn / api_football / manual /
            // gotsport) so we gate on the loose-fallback sentinel `"match"` instead.
            if (entry.source === "match") {
                toast.error("This row has no schedule record — use the rivalry admin's match list to delete it.");
                return;
            }
            await api.siteDeleteSchedule(entry.id);
            toast.success("Schedule entry deleted");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Delete failed");
        }
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        try {
            const r = await api.siteRefreshSchedule(tournamentId);
            toast.success(`Refreshed — ${r.imported_count} imported, ${r.schedule_upserted} scheduled`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Refresh failed");
        }
        setRefreshing(false);
    };

    // iter84.58 (A) — Soft "lock current labels" bulk button.
    // Flips is_manual_override=true on every row that currently has a
    // non-empty round_label. Doesn't change the labels themselves.
    // Safety net for tournaments where (B) misses a row (kickoff
    // outside KO window) but the operator can see the right label
    // already populated.
    const handleLockCurrentLabels = async () => {
        if (!window.confirm(
            "Lock every row that currently shows a round label? Future ESPN polls won't touch them. Use Clear Override on individual rows to unlock.",
        )) return;
        try {
            const r = await api.siteLockCurrentLabels(tournamentId);
            toast.success(`Locked ${r.rows_locked} row(s). ESPN polls will not touch their round labels.`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Lock failed");
        }
    };

    // iter84.58 — Per-match Round Override. Operator picks a round
    // (or empty = clear) to pin the row's `round_label`; backend writes
    // through to `matches.stage` and reruns `recompute_team_stats`.
    const handleOverrideRound = async (entry, choice) => {
        // iter84.59.13 — Gate on `"match"` sentinel (loose-fallback rows);
        // every other source value means the row has a scheduled_matches doc.
        if (entry.source === "match") {
            toast.error("No schedule record — override not available for this row.");
            return;
        }
        try {
            // Empty-string sentinel = clear override.
            const payload = choice === "" ? null : choice;
            const r = await api.siteOverrideMatchRound(tournamentId, entry.id, payload);
            if (r?.cleared) {
                toast.success("Round override cleared — ESPN sync will resume on this row.");
            } else {
                toast.success(`Round pinned to ${choice}. Standings recomputed.`);
            }
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Override failed");
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/85 overflow-y-auto" data-testid="site-admin-schedule-modal">
            <div className="max-w-5xl mx-auto my-8 brutal-card p-6 bg-ink border-black space-y-5">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div>
                        <h2 className="font-display font-black text-2xl uppercase flex items-center gap-2">
                            <Calendar className="w-6 h-6 text-cyber" /> Schedule console
                        </h2>
                        <p className="text-xs text-zinc-400 mt-1">
                            {tournament?.name || "Tournament"} — site-admin edits set <span className="text-blaze font-bold">manual override</span>.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {espnConfigured && (
                            <button
                                data-testid="schedule-refresh-btn"
                                onClick={handleRefresh}
                                disabled={refreshing}
                                className="brutal-btn text-xs py-2 bg-neon text-black border-black shadow-brutal disabled:opacity-40"
                            >
                                <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
                                {refreshing ? "Refreshing…" : "Refresh from ESPN"}
                            </button>
                        )}
                        {espnConfigured && (
                            <button
                                data-testid="schedule-lock-current-labels-btn"
                                onClick={handleLockCurrentLabels}
                                className="brutal-btn text-xs py-2 bg-amber-500 text-black border-black shadow-brutal"
                                title="iter84.58 (A) — Lock all rows that currently show a round label so ESPN polls leave them alone. Use the per-row 'Clear override' option to unlock individually."
                            >
                                <Pin className="w-3.5 h-3.5" /> Lock all labels
                            </button>
                        )}
                        <button
                            data-testid="schedule-add-btn"
                            onClick={() => setEditingEntry("new")}
                            className="brutal-btn text-xs py-2 bg-blaze text-white border-black shadow-brutal"
                        >
                            <Plus className="w-3.5 h-3.5" /> Add game
                        </button>
                        <button
                            data-testid="schedule-close-btn"
                            onClick={onClose}
                            className="brutal-btn-ghost p-2"
                            aria-label="Close"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {loading && <p className="text-xs text-zinc-500">Loading…</p>}

                {/* iter84.60.4.4 — Rebuild from Cache (non-destructive).
                    Preserves overrides. Sits BEFORE HardWipeCard so
                    operators see the safe option first. */}
                {tournament?.espn_sport && tournament?.espn_league && (
                    <RebuildFromCacheCard tournaments={tournament ? [tournament] : []} />
                )}
                {/* iter84.60.4.1 — Hard Wipe (absolute reset). Deletes
                    all fixtures + overrides for the tournament and
                    rebuilds from the current upstream_cache snapshot.
                    Replaces the iter84.59.24 HardRefreshFromEspnCard.
                    Only shown for ESPN-sourced tournaments. */}
                {tournament?.espn_sport && tournament?.espn_league && (
                    <HardWipeCard tournaments={tournament ? [tournament] : []} />
                )}
                {/* iter84.60.5.1.5 — Upstream cache observability.
                    Read-only snapshot timeline. Shown for BOTH ESPN AND
                    API-Football tournaments (unified view — the whole
                    point of the .60 migration). Custom / manual
                    tournaments render a "no upstream" placeholder inside
                    the card, so no gate here. */}
                {tournament && <UpstreamSnapshotsCard tournament={tournament} />}

                {!loading && entries.length === 0 && (
                    <p className="text-xs text-zinc-500" data-testid="schedule-console-empty">
                        No schedule entries yet. Click <span className="text-blaze">Add game</span> to create one
                        {espnConfigured ? <> or <span className="text-neon">Refresh from ESPN</span> to pull the latest fixtures</> : null}.
                    </p>
                )}

                {!loading && entries.length > 0 && (
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs" data-testid="schedule-console-table">
                            <thead>
                                <tr className="text-left text-zinc-500 uppercase text-[10px] tracking-wider">
                                    <th className="p-2">When</th>
                                    <th className="p-2">Round</th>
                                    <th className="p-2">Home</th>
                                    <th className="p-2 text-center">Score</th>
                                    <th className="p-2">Away</th>
                                    <th className="p-2">Status</th>
                                    <th className="p-2">Source</th>
                                    <th className="p-2 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {entries.map(e => (
                                    <tr key={e.id} className="border-t border-zinc-800 hover:bg-zinc-900/50" data-testid={`schedule-console-row-${e.id}`}>
                                        <td className="p-2 whitespace-nowrap">{_dt(e.scheduled_at)}</td>
                                        <td className="p-2">
                                            <div className="flex items-center gap-1.5">
                                                <span className={e.is_manual_override ? "text-blaze font-bold" : ""}>
                                                    {e.round_label || "—"}
                                                </span>
                                                {e.is_manual_override && (
                                                    <Pin className="w-3 h-3 text-blaze shrink-0" aria-label="Manual override" />
                                                )}
                                            </div>
                                            {/* iter84.59.13 — Override-round picker
                                                available for any row with a
                                                scheduled_matches doc (everything except
                                                the loose-fallback `"match"` sentinel). */}
                                            {e.source !== "match" && (
                                                <select
                                                    data-testid={`schedule-override-round-${e.id}`}
                                                    value="__noop__"
                                                    onChange={(ev) => {
                                                        if (ev.target.value === "__noop__") return;
                                                        handleOverrideRound(e, ev.target.value);
                                                        // Reset the dropdown so it's ready for the next action.
                                                        ev.target.value = "__noop__";
                                                    }}
                                                    className="mt-1 bg-black/40 border border-zinc-700 text-[10px] text-zinc-300 px-1 py-0.5 rounded uppercase tracking-wider"
                                                    title="Pin a round (or clear the override)"
                                                >
                                                    <option value="__noop__">Override…</option>
                                                    {OVERRIDE_ROUND_CHOICES.map(r => (
                                                        <option key={r} value={r}>Pin {r}</option>
                                                    ))}
                                                    {e.is_manual_override && (
                                                        <option value="">Clear override</option>
                                                    )}
                                                </select>
                                            )}
                                        </td>
                                        <td className={`p-2 ${e.home_is_tbd ? "italic text-zinc-500" : ""}`}>{e.home_label}</td>
                                        <td className="p-2 text-center font-mono">
                                            {e.played ? `${e.score_a}–${e.score_b}` : "—"}
                                        </td>
                                        <td className={`p-2 ${e.away_is_tbd ? "italic text-zinc-500" : ""}`}>{e.away_label}</td>
                                        <td className="p-2">
                                            <StatusPill status={e.status} played={e.played} />
                                        </td>
                                        <td className="p-2 text-[10px] uppercase">
                                            {/* iter84.59.13 — Render the doc's actual
                                                source field. Pre-fix this hardcoded a
                                                two-way split (`schedule` vs `match`)
                                                and showed `MATCH` for every row once
                                                the backend started returning real
                                                provenance (api_football / espn /
                                                manual / gotsport). */}
                                            {e.is_manual_override ? (
                                                <span className="text-blaze">manual</span>
                                            ) : e.source === "api_football" ? (
                                                <span className="text-cyber">api-football</span>
                                            ) : e.source === "espn" ? (
                                                <span className="text-cyber">espn</span>
                                            ) : e.source === "gotsport" ? (
                                                <span className="text-cyber">gotsport</span>
                                            ) : e.source === "manual" ? (
                                                <span className="text-blaze">manual</span>
                                            ) : (
                                                <span className="text-zinc-500">match</span>
                                            )}
                                        </td>
                                        <td className="p-2 text-right whitespace-nowrap">
                                            <button
                                                data-testid={`schedule-edit-${e.id}-btn`}
                                                onClick={() => setEditingEntry(e)}
                                                disabled={e.source === "match"}
                                                className="brutal-btn-ghost text-xs p-1.5 disabled:opacity-30"
                                                title={e.source === "match" ? "No schedule record — edit via rivalry admin's match list" : "Edit entry"}
                                            >
                                                <Edit2 className="w-3.5 h-3.5" />
                                            </button>
                                            <button
                                                data-testid={`schedule-delete-${e.id}-btn`}
                                                onClick={() => handleDelete(e)}
                                                className="brutal-btn-ghost text-xs p-1.5 text-blaze"
                                                title="Delete"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {editingEntry && (
                    <ScheduleEditDialog
                        entry={editingEntry === "new" ? null : editingEntry}
                        tournament={tournament}
                        tournamentId={tournamentId}
                        onClose={() => setEditingEntry(null)}
                        onSaved={() => { setEditingEntry(null); load(); }}
                    />
                )}
            </div>
        </div>
    );
};

const StatusPill = ({ status, played }) => {
    if (status === "live") return <span className="text-[9px] font-bold uppercase tracking-wider text-neon">● LIVE</span>;
    if (played || status === "post") return <span className="text-[9px] font-bold uppercase tracking-wider text-zinc-500">FINAL</span>;
    if (status === "cancelled") return <span className="text-[9px] font-bold uppercase tracking-wider text-blaze">CANCELLED</span>;
    return <span className="text-[9px] font-bold uppercase tracking-wider text-zinc-600">SCHEDULED</span>;
};

const ScheduleEditDialog = ({ entry, tournament, tournamentId, leagueId, onClose, onSaved, apiMode = "site-admin" }) => {
    const isNew = !entry;
    const teams = tournament?.teams || [];

    const [homeTeamId, setHomeTeamId] = useState(entry?.home_team_id || "");
    const [homeName, setHomeName] = useState(entry?.home_team_id ? "" : (entry?.home_label || ""));
    const [awayTeamId, setAwayTeamId] = useState(entry?.away_team_id || "");
    const [awayName, setAwayName] = useState(entry?.away_team_id ? "" : (entry?.away_label || ""));
    const [scheduledAt, setScheduledAt] = useState(_toLocalInput(entry?.scheduled_at) || "");
    const [roundLabel, setRoundLabel] = useState(entry?.round_label || "");
    const [status, setStatus] = useState(entry?.status || "scheduled");
    const [scoreA, setScoreA] = useState(entry?.score_a ?? "");
    const [scoreB, setScoreB] = useState(entry?.score_b ?? "");
    // iter84.59 — Optional PK scoreline for KO games that ended tied in
    // regulation. Required by the backend whenever stage != group AND
    // scoreA == scoreB; otherwise the submit is rejected. Penalty inputs
    // are owned by the row editor now (iter86.83 — orphaned state removed).
    // iter84.59.23 — Explicit result picker for knockout games. The
    // backend refuses to guess a KO winner from scores alone (iter84.59.12
    // — "score arithmetic is NEVER used to decide a KO winner"). Before
    // this iter the Edit Game dialog had NO way to supply `winner_team_id`,
    // so any non-ESPN KO result was un-recordable. Options:
    //   ""       → unset (default; blocks save if stage is KO)
    //   "home"   → home team advances
    //   "away"   → away team advances
    //   "pk"     → tied in regulation, PK decides (reveals penalty inputs)
    const [koResult, setKoResult] = useState(() => {
        // Initialise from an already-linked winner if we're editing.
        // ESPN-imported games have winner_team_id (or is_draw) set;
        // this preselects the matching radio so the operator can eyeball
        // and either accept or override.
        if (entry?.is_draw) return "tie";
        const existingWinner = entry?.winner_team_id || entry?.winner_id;
        if (!existingWinner) return "";
        if (existingWinner === entry?.home_team_id) return "home";
        if (existingWinner === entry?.away_team_id) return "away";
        return "";
    });
    // iter84.59.7 — Initialise the stage dropdown from the existing
    // entry's round_label (or the linked match's stage) so an operator
    // editing a R32 game doesn't accidentally re-save it as Group just
    // because they touched the scores. Pre-fix the dropdown always
    // defaulted to "group" — combined with the score-edit path's
    // delete+reinsert, this is the single most common path that
    // produced the "Brazil credited with a phantom group game" bug.
    const _initialStage = (() => {
        const raw = (entry?.stage || entry?.round_label || "").toString().trim().toLowerCase();
        if (!raw) return "group";
        const map = {
            "group": "group", "group stage": "group", "regular season": "group",
            "r64": "r64", "round of 64": "r64",
            "r32": "r32", "round of 32": "r32",
            "r16": "r16", "round of 16": "r16",
            "qf": "qf", "quarterfinal": "qf", "quarterfinals": "qf",
            "sf": "sf", "semifinal": "sf", "semifinals": "sf",
            "final": "final",
        };
        return map[raw] || "group";
    })();
    const [stage, setStage] = useState(_initialStage);
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        if (!scheduledAt) {
            toast.error("Kickoff time is required");
            return;
        }
        setSaving(true);
        try {
            const body = {
                home_team_id: homeTeamId || undefined,
                home_name: homeTeamId ? undefined : (homeName || undefined),
                away_team_id: awayTeamId || undefined,
                away_name: awayTeamId ? undefined : (awayName || undefined),
                scheduled_at: _fromLocalInput(scheduledAt),
                round_label: roundLabel || undefined,
                status,
            };
            if (scoreA !== "" && scoreB !== "") {
                body.score_a = parseInt(scoreA, 10);
                body.score_b = parseInt(scoreB, 10);
                body.stage = stage;
                // iter84.59.23 — Explicit winner picker drives the outcome
                // for EVERY match, KO or group. Scores are cosmetic.
                //   koResult "home"  → winner_team_id = home_team_id
                //   koResult "away"  → winner_team_id = away_team_id
                //   koResult "tie"   → is_draw = true (group stages only)
                //   koResult ""      → must fill it in before saving
                if (!koResult) {
                    throw new Error(
                        "Pick a Result — Home wins, Away wins, or Tie. "
                        + "Scores alone don't decide anything; the picker does."
                    );
                }
                if (koResult === "home") {
                    body.winner_team_id = homeTeamId || undefined;
                } else if (koResult === "away") {
                    body.winner_team_id = awayTeamId || undefined;
                } else if (koResult === "tie") {
                    body.is_draw = true;
                }
            }
            if (isNew) {
                if (apiMode === "league-admin") {
                    await api.leagueAddSchedule(leagueId, body);
                } else {
                    await api.siteAddSchedule(tournamentId, body);
                }
                toast.success("Schedule entry added");
            } else if (entry.source === "match" && apiMode === "league-admin") {
                // Legacy match-only row (no scheduled_matches record) — route the
                // edit through the existing match-update endpoint. Stats are
                // reverted/re-applied server-side so the leaderboard stays correct.
                if (body.score_a === undefined || body.score_b === undefined) {
                    throw new Error("Scores are required when editing a previously played match.");
                }
                if (!body.home_team_id || !body.away_team_id) {
                    throw new Error("Both teams must be selected (not free-text) to edit a played match.");
                }
                await api.updateMatch(entry.match_id || entry.id, {
                    stage: body.stage || "group",
                    team_a_id: body.home_team_id,
                    team_b_id: body.away_team_id,
                    score_a: body.score_a,
                    score_b: body.score_b,
                    penalty_a: body.penalty_a,
                    penalty_b: body.penalty_b,
                    // iter84.59.23 — Thread explicit winner through the
                    // legacy match-only edit path too.
                    winner_team_id: body.winner_team_id,
                });
                toast.success("Match updated");
            } else {
                if (apiMode === "league-admin") {
                    await api.leagueEditSchedule(leagueId, entry.id, body);
                } else {
                    await api.siteEditSchedule(entry.id, body);
                }
                toast.success("Schedule entry updated");
            }
            onSaved();
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message || "Save failed");
        }
        setSaving(false);
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center px-4" data-testid="schedule-edit-dialog">
            <div className="brutal-card p-6 max-w-2xl w-full bg-ink border-black space-y-4 max-h-[90dvh] overflow-y-auto">
                <div className="flex items-center justify-between">
                    <h3 className="font-display font-black text-xl uppercase">{isNew ? "Add game" : "Edit game"}</h3>
                    <button onClick={onClose} className="brutal-btn-ghost p-2"><X className="w-4 h-4" /></button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <TeamPicker label="Home team" teams={teams} teamId={homeTeamId}
                                setTeamId={setHomeTeamId} freeText={homeName} setFreeText={setHomeName}
                                testIdPrefix="home" />
                    <TeamPicker label="Away team" teams={teams} teamId={awayTeamId}
                                setTeamId={setAwayTeamId} freeText={awayName} setFreeText={setAwayName}
                                testIdPrefix="away" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-[10px] uppercase text-zinc-500 font-bold mb-1">Kickoff (local)</label>
                        <input
                            data-testid="schedule-edit-when"
                            type="datetime-local"
                            value={scheduledAt}
                            onChange={(e) => setScheduledAt(e.target.value)}
                            className="brutal-input w-full"
                        />
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase text-zinc-500 font-bold mb-1">Round</label>
                        <select
                            data-testid="schedule-edit-round"
                            value={roundLabel || ""}
                            onChange={(e) => setRoundLabel(e.target.value)}
                            className="brutal-input w-full"
                        >
                            <option value="">— Unspecified —</option>
                            {ROUNDS.map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase text-zinc-500 font-bold mb-1">Status</label>
                        <select
                            data-testid="schedule-edit-status"
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                            className="brutal-input w-full"
                        >
                            {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                    </div>
                </div>

                <div className="brutal-card-light p-3 space-y-2">
                    <p className="text-[10px] uppercase text-zinc-500 font-bold">Score override (optional — sets status to Final)</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                            <label className="block text-[10px] uppercase text-zinc-500 mb-1">Home score</label>
                            <input
                                data-testid="schedule-edit-score-a"
                                type="number"
                                value={scoreA}
                                onChange={(e) => setScoreA(e.target.value)}
                                className="brutal-input w-full"
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] uppercase text-zinc-500 mb-1">Away score</label>
                            <input
                                data-testid="schedule-edit-score-b"
                                type="number"
                                value={scoreB}
                                onChange={(e) => setScoreB(e.target.value)}
                                className="brutal-input w-full"
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] uppercase text-zinc-500 mb-1">Stage (for scoring)</label>
                            <select
                                data-testid="schedule-edit-stage"
                                value={stage}
                                onChange={(e) => setStage(e.target.value)}
                                className="brutal-input w-full"
                            >
                                <option value="group">Group stage</option>
                                <option value="r64">Round of 64</option>
                                <option value="r32">Round of 32</option>
                                <option value="r16">Round of 16</option>
                                <option value="qf">Quarterfinal</option>
                                <option value="sf">Semifinal</option>
                                <option value="final">Final</option>
                            </select>
                        </div>
                    </div>
                    <p className="text-[10px] text-zinc-500 italic">Leave blank to keep the game unplayed. Both teams must be matched (selected from the roster) to record a score.</p>
                </div>

                {/* iter84.59.23 — Explicit Winner picker for EVERY match.
                    The picker is the single source of truth for outcomes;
                    scores are cosmetic. For ESPN-imported games the
                    linked match's winner_team_id / is_draw preselects
                    the matching radio — site admin can override.
                     * KO stages: Home / Away (no ties in bracket play).
                     * Group / regular: Home / Away / Tie. */}
                {scoreA !== "" && scoreB !== "" && (
                    <div className="brutal-card-light p-3 space-y-2 border-emerald-500 border-2" data-testid="schedule-edit-result-panel">
                        <p className="text-[10px] uppercase text-emerald-400 font-bold">
                            Result — required
                        </p>
                        <div className={`grid ${stage === "group" || stage === "regular" ? "grid-cols-3" : "grid-cols-2"} gap-2`}>
                            <label className={`flex items-center gap-2 p-2 border-2 cursor-pointer text-xs ${koResult === "home" ? "border-emerald-500 bg-emerald-500/10" : "border-zinc-700 hover:border-zinc-500"}`} data-testid="schedule-edit-result-home">
                                <input
                                    type="radio"
                                    name="ko-result"
                                    value="home"
                                    checked={koResult === "home"}
                                    onChange={() => setKoResult("home")}
                                    className="accent-emerald-500"
                                />
                                <span>{homeName || "Home"} wins</span>
                            </label>
                            {(stage === "group" || stage === "regular") && (
                                <label className={`flex items-center gap-2 p-2 border-2 cursor-pointer text-xs ${koResult === "tie" ? "border-blaze bg-blaze/10" : "border-zinc-700 hover:border-zinc-500"}`} data-testid="schedule-edit-result-tie">
                                    <input
                                        type="radio"
                                        name="ko-result"
                                        value="tie"
                                        checked={koResult === "tie"}
                                        onChange={() => setKoResult("tie")}
                                        className="accent-blaze"
                                    />
                                    <span>Tie</span>
                                </label>
                            )}
                            <label className={`flex items-center gap-2 p-2 border-2 cursor-pointer text-xs ${koResult === "away" ? "border-emerald-500 bg-emerald-500/10" : "border-zinc-700 hover:border-zinc-500"}`} data-testid="schedule-edit-result-away">
                                <input
                                    type="radio"
                                    name="ko-result"
                                    value="away"
                                    checked={koResult === "away"}
                                    onChange={() => setKoResult("away")}
                                    className="accent-emerald-500"
                                />
                                <span>{awayName || "Away"} wins</span>
                            </label>
                        </div>
                        <p className="text-[10px] text-zinc-500 italic">
                            The picker drives who gets W / D / L. Scores are cosmetic — they never derive the outcome.
                        </p>
                    </div>
                )}

                {/* iter84.59.23 — PK inputs removed. The winner picker
                    above is the single source of truth for outcomes.
                    Displaying PK scoreline is a future cosmetic pass;
                    it doesn't drive anything today. */}

                <div className="flex justify-end gap-2 pt-2">
                    <button onClick={onClose} className="brutal-btn-ghost text-xs" disabled={saving}>Cancel</button>
                    <button
                        data-testid="schedule-edit-save-btn"
                        onClick={handleSave}
                        disabled={saving}
                        className="brutal-btn text-xs bg-blaze text-white border-black shadow-brutal disabled:opacity-40"
                    >
                        <Save className="w-3.5 h-3.5" />
                        {saving ? "Saving…" : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
};

const TeamPicker = ({ label, teams, teamId, setTeamId, freeText, setFreeText, testIdPrefix }) => (
    <div>
        <label className="block text-[10px] uppercase text-zinc-500 font-bold mb-1">{label}</label>
        <select
            data-testid={`schedule-edit-${testIdPrefix}-team-select`}
            value={teamId}
            onChange={(e) => { setTeamId(e.target.value); if (e.target.value) setFreeText(""); }}
            className="brutal-input w-full mb-1"
        >
            <option value="">— Free text / TBD —</option>
            {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        {!teamId && (
            <input
                data-testid={`schedule-edit-${testIdPrefix}-team-name`}
                type="text"
                value={freeText}
                onChange={(e) => setFreeText(e.target.value)}
                className="brutal-input w-full text-xs"
                placeholder="e.g. Winner of SF1, TBD…"
            />
        )}
    </div>
);

export default SiteAdminSchedule;
export { ScheduleEditDialog };
